import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarCheck, RefreshCw, Link2, Unlink, AlertTriangle } from "lucide-react";
import { adminClient } from "./api";

export default function CalendarSyncCard() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await adminClient.get("/admin/calendar/status"); setS(data); }
    catch { setS(null); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const flag = params.get("calendar");
    if (!flag) return;
    if (flag === "connected") toast.success(`Google Calendar connected${params.get("email") ? ` as ${params.get("email")}` : ""} — syncing your jobs now`, { duration: 7000 });
    else toast.error(`Google Calendar: ${params.get("msg") || "connection failed"}`, { duration: 10000 });
    params.delete("calendar"); params.delete("email"); params.delete("msg");
    setParams(params, { replace: true });
  }, [params, setParams]);

  const connect = async () => {
    setBusy("connect");
    try {
      const { data } = await adminClient.post("/admin/calendar/auth-url", { site_url: window.location.origin, return_to: "/admin/dashboard" });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start Google sign-in"); setBusy(""); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Synced job events will be removed from your calendar.")) return;
    setBusy("disconnect");
    try { const { data } = await adminClient.post("/admin/calendar/disconnect"); toast.success(`Disconnected · removed ${data.cleared} events`); load(); }
    catch { toast.error("Could not disconnect"); } finally { setBusy(""); }
  };

  const syncNow = async () => {
    setBusy("sync");
    try {
      const { data } = await adminClient.post("/admin/calendar/sync");
      toast.success(`Synced ${data.total} jobs · ${data.created} added · ${data.updated} updated · ${data.deleted} removed${data.error ? ` · ${data.error} failed` : ""}`, { duration: 7000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); } finally { setBusy(""); }
  };

  const connected = !!s?.connected;
  const statusLabel = !s?.configured ? "NOT CONFIGURED" : connected ? "CONNECTED" : "NOT CONNECTED";
  const hint = !s?.configured
    ? "Add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to backend/.env, then restart the backend."
    : s?.last_error || "";

  return (
    <Card data-testid="calendar-sync-card" className="p-5 border-2 border-zinc-200">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarCheck className="w-4 h-4 text-[#0C2E2B]" />
          <h3 className="font-display font-black text-[#0C2E2B]">Google Calendar</h3>
        </div>
        <button data-testid="calendar-refresh-btn" onClick={load} className="text-zinc-400 hover:text-[#0C2E2B]">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {loading && !s ? <div className="text-xs text-zinc-500 mt-3">Checking…</div> : !s ? (
        <div className="text-xs text-red-600 mt-3">Could not load calendar status</div>
      ) : (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2">
            <Badge data-testid="calendar-status-badge" className={`text-[10px] ${connected ? "bg-emerald-600 hover:bg-emerald-700" : "bg-zinc-400 hover:bg-zinc-500"}`}>{statusLabel}</Badge>
            {connected && <span data-testid="calendar-account" className="text-[11px] font-mono-data font-bold text-[#0C2E2B] truncate">{s.email || "Google account"}</span>}
          </div>
          <p className="text-[11px] text-zinc-500 leading-snug">
            Every <b>scheduled</b> or <b>in-progress</b> job lands on your calendar with the arrival window, address and customer details. Cancelled or refunded jobs are removed automatically; completed ones get a ✓.
          </p>
          {connected && (
            <div className="text-[11px] text-zinc-600 flex justify-between" data-testid="calendar-sync-stats">
              <span>{s.synced_count} job{s.synced_count === 1 ? "" : "s"} on calendar</span>
              <span>{s.last_sync_at ? `synced ${new Date(s.last_sync_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : "auto-sync every 15 min"}</span>
            </div>
          )}
          {hint && (
            <div data-testid="calendar-hint" className="flex items-start gap-1.5 text-[10px] text-amber-800 bg-amber-50 border-l-2 border-amber-500 px-2 py-1.5 rounded-r">
              <AlertTriangle className="w-3 h-3 mt-px shrink-0" /> <span>{hint}</span>
            </div>
          )}
          {!connected ? (
            <Button data-testid="calendar-connect-btn" onClick={connect} disabled={!s.configured || busy === "connect"} className="w-full rounded-md text-[11px] h-9 bg-[#0C2E2B] text-[#FFDF00] hover:bg-[#0C2E2B]/90 font-black">
              <Link2 className="w-3 h-3 mr-1" /> {busy === "connect" ? "Opening Google…" : "Connect Google Calendar"}
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button data-testid="calendar-sync-now-btn" onClick={syncNow} disabled={!!busy} variant="outline" className="flex-1 rounded-md text-[11px] h-9 font-bold">
                <RefreshCw className={`w-3 h-3 mr-1 ${busy === "sync" ? "animate-spin" : ""}`} /> {busy === "sync" ? "Syncing…" : "Sync now"}
              </Button>
              <Button data-testid="calendar-disconnect-btn" onClick={disconnect} disabled={!!busy} variant="outline" className="rounded-md text-[11px] h-9 text-red-700 border-red-200 hover:bg-red-50">
                <Unlink className="w-3 h-3 mr-1" /> Disconnect
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
